import {
  MagicFill,
  Revenue,
  AIContentCreatorIcon,
  TextAddIcon,
} from '../icons';
import type { CardItem } from '../ui/Card';

export const ADVERTISER_START_CARDS: Omit<CardItem, 'icon' | 'addIcon'>[] = [
  {
    id: 'EVENT_CAMPAIGN',
    title: 'Start Campaign with Event',
    desc: 'Launch ads triggered by specific events or actions',
  },
  // {
  //   id: 'TIME_CAMPAIGN',
  //   title: 'Start Campaign by Time',
  //   desc: 'Schedule your ads to run automatically at set times',
  // },
];

export const ADVERTISER_CARD_ICONS = {
  EVENT_CAMPAIGN: {
    icon: MagicFill,
    addIcon: AIContentCreatorIcon,
  },
  TIME_CAMPAIGN: {
    icon: Revenue,
    addIcon: TextAddIcon,
  },
};
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { isProd } from '../utils';
import { isDefaultCreative } from './creativeTypes';
import { validateTimeRanges } from '../../features/dooh/campaigns/utils/schedulingValidation';
import { DAY_ORDER } from '@/features/dooh/screen/constants';

const campaignStartDateSchema = Yup.date()
  .typeError('Please enter campaign date range')
  .required('Please enter campaign start date and end date')
  .test(
    'start-date-locked',
    'Start date cannot be changed as the campaign has already started or starts today.',
    function (value) {
      const { isEditMode, originalStartDate } =
        (this.options as any).context ?? {};
      if (!isEditMode || !originalStartDate) return true;
      const isLocked =
        dayjs(originalStartDate).isSame(dayjs().startOf('day'), 'day') ||
        dayjs(originalStartDate).isBefore(dayjs().startOf('day'), 'day');
      if (!isLocked) return true;
      const incoming = value ? dayjs(value).format('YYYY-MM-DD') : '';
      return incoming === originalStartDate;
    }
  );

export const STEPS = [
  { heading: 'Step 1', subheading: 'Basic Details' },
  { heading: 'Step 2', subheading: 'Touchpoint Selection' },
  { heading: 'Step 3', subheading: 'Screen Selection' },
  { heading: 'Step 4', subheading: 'Screen SOV' },
  { heading: 'Step 5', subheading: 'Creative Upload' },
  { heading: 'Step 6', subheading: 'Preview & Confirm' },
] as const;

export const STEP_SCHEMAS = [
  Yup.object().shape({
    name: Yup.string().required('Campaign name required'),
    campaignBudget: Yup.number()
      .positive('Budget is required')
      .required('Budget required'),
    campaignStartDate: campaignStartDateSchema,
    campaignEndDate: Yup.date()
      .typeError('Please enter campaign date range')
      .test(
        'valid-duration',
        'End date/time must be after start date/time',
        function (endDate) {
          const { campaignStartDate } = this.parent;

          if (!campaignStartDate || !endDate) {
            return true;
          }

          return (
            new Date(endDate).getTime() > new Date(campaignStartDate).getTime()
          );
        }
      )
      .required('please enter campaign start date and end date'),
    selectedCityGroup: Yup.array()
      .of(Yup.string())
      .min(1, 'Please select at least one state to continue')
      .required('Please select a state to continue'),
    selectedBrand: Yup.object()
      .nullable()
      .required('Please select a brand folder to continue'),
    timeSlots: Yup.array().when('campaignType', {
      is: 'TIME_CAMPAIGN',
      then: schema => schema.min(1, 'Select at least one time range'),
      otherwise: schema => schema.notRequired(),
    }),
    scheduling: Yup.object()
      .nullable()
      .test(
        'scheduling-validation',
        'Invalid scheduling configuration',
        function (value) {
          if (!value) return true;
          const state = value as any;

          if (
            state.enableDayParting &&
            (!state.selectedDays || state.selectedDays.length === 0)
          ) {
            return this.createError({
              message: 'Please select at least one day for Day Partitioning',
            });
          }

          if (state.enableTimeParting) {
            const hasAllDaysSlots = (state.allDays || []).some(
              (r: any) => r.start && r.end
            );

            const hasPerDaySlots = Object.values(
              state.perDaySchedules || {}
            ).some(
              (day: any) =>
                day?.enabled && day?.ranges?.some((r: any) => r.start && r.end)
            );

            if (!hasAllDaysSlots && !hasPerDaySlots) {
              return this.createError({
                message:
                  'Please configure at least one time slot in All Days or Per-Day Overrides',
              });
            }
            if (state.enableDayParting && !hasAllDaysSlots) {
              const missingDays = (state.selectedDays || []).filter(
                (day: any) => {
                  const schedule = state.perDaySchedules?.[day];

                  return !(
                    schedule?.enabled &&
                    schedule?.ranges?.some((r: any) => r.start && r.end)
                  );
                }
              );

              if (missingDays.length > 0) {
                return this.createError({
                  message: 'Please configure time slots for all selected days',
                });
              }
            }

            if (hasAllDaysSlots) {
              const hasAllDaysRanges = (state.allDays || []).some(
                (r: any) => r.start && r.end
              );

              if (hasAllDaysRanges) {
                const allDaysError = validateTimeRanges(state.allDays || []);

                if (allDaysError) {
                  return this.createError({
                    message: `All Days: ${allDaysError}`,
                  });
                }
              }
            }

            const weekdays = DAY_ORDER.map(day => day.toLowerCase());

            for (const day of weekdays) {
              if (state.perDaySchedules?.[day]?.enabled) {
                const ranges = state.perDaySchedules[day].ranges || [];

                if (ranges.length > 0) {
                  const dayError = validateTimeRanges(ranges);

                  if (dayError) {
                    return this.createError({
                      message: `${day.charAt(0).toUpperCase() + day.slice(1)}: ${dayError}`,
                    });
                  }
                }
              }
            }
          }

          return true;
        }
      ),
  }),
  // Step 2: Target Group Selection - Require at least one touchpoint selected
  Yup.object().shape({
    selectedTouchpoints: Yup.array()
      .min(1, 'Please select at least one touchpoint to continue')
      .required('Touchpoint selection is required'),
  }),
  // Step 3: Screen Selection
  Yup.object({
    targetGroups: Yup.array()
      .min(1, 'At least one target group is required')
      .of(
        Yup.object({
          title: Yup.string().required(),
          selectedArea: Yup.array()
            .min(1, 'Each target group must have at least one selected area')
            .required('Selected area is required'),
        })
      ),
  }),
  // Step 4: Screen SOV
  Yup.object().shape({
    targetGroups: Yup.array()
      .min(1, 'At least one target group is required')
      .of(
        Yup.object({
          selectedArea: Yup.array()
            .min(1, 'Each target group must have at least one selected area')
            .required('Selected area is required'),
        })
      ),
  }),
  // 5th step validator - Creative Upload: Validate all slots have default creatives
  Yup.object().shape({
    selectedScreens: Yup.array()
      .min(1, 'At least one screen must be selected')
      .test(
        'all-slots-have-default',
        'All slots must have a default creative before proceeding',
        function (selectedScreens) {
          if (!selectedScreens || selectedScreens.length === 0) {
            return this.createError({
              message: 'No screens selected',
            });
          }

          const errors: string[] = [];
          selectedScreens.forEach((screen: any) => {
            if (!screen.slots || screen.slots.length === 0) {
              errors.push(`${screen.name || 'Screen'} has no slots configured`);
            } else {
              screen.slots.forEach((slot: any, slotIndex: number) => {
                // A default creative can be: CREATIVE_TYPE.DEFAULT, CREATIVE_TYPE.VAST, or CREATIVE_TYPE.WEB
                const hasDefaultCreative = slot.creatives?.some(
                  (creative: any) => isDefaultCreative(creative?.type)
                );
                if (!hasDefaultCreative) {
                  errors.push(
                    `${screen.name || 'Screen'} - Slot ${slotIndex + 1} (${slot.name || 'Unnamed'}) is missing a default creative`
                  );
                }
              });
            }
          });

          if (errors.length > 0) {
            return this.createError({
              message: errors.join('; '),
            });
          }

          return true;
        }
      ),
  }),
  // Preview step validator (no new fields; always passes)
  Yup.object().shape({}),
] as const;

export const API_ENDPOINTS = {
  FILTERS: '/dooh_api/screen/v1/advertiser/devices/filters',
  DEVICES: isProd
    ? '/dooh_api/screen/v1/devices/list?offset=0&limit=700'
    : '/dooh_api/screen/v1/advertiser/devices/list?offset=0&limit=60',
} as const;

export const ERROR_MESSAGES = {
  VALIDATION_FAILED: 'Please fix the validation errors before proceeding',
  API_ERROR: 'Something went wrong. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
} as const;

// Budget constants
export const BUDGET_CONSTANTS = {
  MIN: 25000,
  MAX: 100000000,
  STEP: 10000,
} as const;

export const DEFAULT_SCREEN_IMAGE =
  'https://images.justbaat.com/city-group/default-screen.jpeg';

export const DEFAULT_TOUCHPOINT_IMAGE =
  'https://images.justbaat.com/city-group/touchpoint.jpeg';

// Currency formatter
export const CURRENCY_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

// Screen Selection constants
export const SCREEN_SELECTION_CONSTANTS = {
  MAP_DEFAULT_CENTER: { lat: 20.5937, lng: 78.9629 },
  MAP_DEFAULT_ZOOM: 5,
  MAP_ZOOM_WITH_SCREENS: 13,
  LOADING_TIMEOUT: 800,
  DEBOUNCE_DELAY: 800,
  SCREEN_LIMIT: 60,
  SCREEN_OFFSET: 0,
} as const;

// Map marker icons
export const MAP_MARKER_ICONS = {
  SELECTED: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
  UNSELECTED: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
} as const;

// Filter configuration
export const FILTER_CONFIG = {
  EXCLUDED_KEYS: ['address.state', 'address.city'],
  FILTER_TYPE: 'terms',
} as const;

// Google Maps API Key
export const MAPS_API_KEY = 'AIzaSyAFODYsSAjlq5gB118fOvoHnVbHidJX-00';

// Available Events for Event-Based Campaigns
export const AVAILABLE_EVENTS = [
  {
    id: 1,
    name: 'Black Friday',
    description: 'Annual shopping event',
    date: '2024-11-29',
  },
  {
    id: 2,
    name: 'Christmas',
    description: 'Holiday celebration',
    date: '2024-12-25',
  },
  {
    id: 3,
    name: 'New Year',
    description: 'Year-end celebration',
    date: '2025-01-01',
  },
  {
    id: 4,
    name: "Valentine's Day",
    description: 'Romantic holiday',
    date: '2025-02-14',
  },
  {
    id: 5,
    name: 'Easter',
    description: 'Spring holiday',
    date: '2025-04-20',
  },
  {
    id: 6,
    name: "Mother's Day",
    description: 'Mother appreciation day',
    date: '2025-05-11',
  },
] as const;

export const STEP_ERROR_MESSAGES: Record<number, string> = {
  1: 'Please add at least one target group before proceeding.',
  2: 'Please select at least one screen before proceeding.',
  4: 'Please fill all slots with creatives before proceeding to the next step.',
};

// ─── OPS / SUPER_ADMIN shrunk flow (4 steps) ───────────────────────────
export const OPS_STEPS = [
  { heading: 'Step 1', subheading: 'Basic Details & Location' },
  { heading: 'Step 2', subheading: 'Screen Selection & SOV' },
  { heading: 'Step 3', subheading: 'Add Creatives' },
  { heading: 'Step 4', subheading: 'Preview & Confirm' },
] as const;

export const OPS_STEP_SCHEMAS = [
  // Step 1: Basic Details & Location (same as advertiser step 1)
  Yup.object().shape({
    name: Yup.string().required('Campaign name required'),
    campaignBudget: Yup.number()
      .positive('Budget is required')
      .required('Budget required'),
    campaignStartDate: campaignStartDateSchema,
    campaignEndDate: Yup.date()
      .typeError('Please enter campaign date range')
      .test(
        'valid-duration',
        'End date/time must be after start date/time',
        function (endDate) {
          const { campaignStartDate } = this.parent;
          if (!campaignStartDate || !endDate) {
            return true;
          }
          return (
            new Date(endDate).getTime() > new Date(campaignStartDate).getTime()
          );
        }
      )
      .required('please enter campaign start date and end date'),
    selectedCityGroup: Yup.array()
      .of(Yup.string())
      .min(1, 'Please select at least one state to continue')
      .required('Please select a state to continue'),
    selectedBrand: Yup.object()
      .nullable()
      .required('Please select a brand folder to continue'),
    scheduling: Yup.object()
      .nullable()
      .test(
        'scheduling-validation',
        'Invalid scheduling configuration',
        function (value) {
          if (!value) return true;
          const state = value as any;

          if (
            state.enableDayParting &&
            (!state.selectedDays || state.selectedDays.length === 0)
          ) {
            return this.createError({
              message: 'Please select at least one day for Day Partitioning',
            });
          }

          if (state.enableTimeParting) {
            const hasAllDaysSlots = (state.allDays || []).some(
              (r: any) => r.start && r.end
            );

            const hasPerDaySlots = Object.values(
              state.perDaySchedules || {}
            ).some(
              (day: any) =>
                day?.enabled && day?.ranges?.some((r: any) => r.start && r.end)
            );

            if (!hasAllDaysSlots && !hasPerDaySlots) {
              return this.createError({
                message:
                  'Please configure at least one time slot in All Days or Per-Day Overrides',
              });
            }
            if (state.enableDayParting && !hasAllDaysSlots) {
              const missingDays = (state.selectedDays || []).filter(
                (day: any) => {
                  const schedule = state.perDaySchedules?.[day];

                  return !(
                    schedule?.enabled &&
                    schedule?.ranges?.some((r: any) => r.start && r.end)
                  );
                }
              );

              if (missingDays.length > 0) {
                return this.createError({
                  message: 'Please configure time slots for all selected days',
                });
              }
            }

            if (hasAllDaysSlots) {
              const hasAllDaysRanges = (state.allDays || []).some(
                (r: any) => r.start && r.end
              );
              if (hasAllDaysRanges) {
                const allDaysError = validateTimeRanges(state.allDays || []);

                if (allDaysError) {
                  return this.createError({
                    message: `All Days: ${allDaysError}`,
                  });
                }
              }
            }

            const weekdays = DAY_ORDER.map(day => day.toLowerCase());

            for (const day of weekdays) {
              if (state.perDaySchedules?.[day]?.enabled) {
                const ranges = state.perDaySchedules[day].ranges || [];

                if (ranges.length > 0) {
                  const dayError = validateTimeRanges(ranges);

                  if (dayError) {
                    return this.createError({
                      message: `${day.charAt(0).toUpperCase() + day.slice(1)}: ${dayError}`,
                    });
                  }
                }
              }
            }
          }

          return true;
        }
      ),
  }),
  // Step 2: Screen Selection & SOV (merged — at least one screen selected)
  Yup.object().shape({
    targetGroups: Yup.array()
      .min(1, 'At least one target group is required')
      .of(
        Yup.object({
          selectedArea: Yup.array()
            .min(1, 'Each target group must have at least one selected area')
            .required('Selected area is required'),
        })
      ),
  }),
  // Step 3: Creative Upload
  Yup.object().shape({
    selectedScreens: Yup.array()
      .min(1, 'At least one screen must be selected')
      .test(
        'all-slots-have-default',
        'All slots must have a default creative before proceeding',
        function (selectedScreens) {
          if (!selectedScreens || selectedScreens.length === 0) {
            return this.createError({ message: 'No screens selected' });
          }
          const errors: string[] = [];
          selectedScreens.forEach((screen: any) => {
            if (!screen.slots || screen.slots.length === 0) {
              errors.push(`${screen.name || 'Screen'} has no slots configured`);
            } else {
              screen.slots.forEach((slot: any, slotIndex: number) => {
                const hasDefaultCreative = slot.creatives?.some(
                  (creative: any) => isDefaultCreative(creative?.type)
                );
                if (!hasDefaultCreative) {
                  errors.push(
                    `${screen.name || 'Screen'} - Slot ${slotIndex + 1} (${slot.name || 'Unnamed'}) is missing a default creative`
                  );
                }
              });
            }
          });
          if (errors.length > 0) {
            return this.createError({ message: errors.join('; ') });
          }
          return true;
        }
      ),
  }),
  // Step 4: Preview (always passes)
  Yup.object().shape({}),
] as const;

export const OPS_STEP_ERROR_MESSAGES: Record<number, string> = {
  1: 'Please select at least one screen before proceeding.',
  2: 'Please fill all slots with creatives before proceeding to the next step.',
};

/** Returns true for roles that see the shrunk 4-step ops flow */
export const isOpsRole = (role?: string): boolean =>
  role === 'DOOH_OPS' || role === 'DOOH_SUPER_ADMIN';
